"use client"

import { Box, Container, Heading, Text, SimpleGrid, Flex, Badge, useColorModeValue } from "@chakra-ui/react"
import { motion, useInView } from "framer-motion"
import { useRef } from "react"
import {
    BoltIcon,
    StarIcon,
    ShieldCheckIcon,
    HeartIcon,
    MagnifyingGlassIcon,
    AcademicCapIcon,
    ClipboardDocumentCheckIcon,
    LightBulbIcon,
} from "@heroicons/react/24/outline"

const MotionBox = motion(Box)
const MotionSimpleGrid = motion(SimpleGrid)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)
const MotionFlex = motion(Flex)

const benefits = [
    {
        title: "Time Efficiency",
        description: "Reduce your time-to-hire by up to 50% with our streamlined recruitment process",
        icon: <BoltIcon className="w-8 h-8" />,
        color: "blue",
    },
    {
        title: "Quality Candidates",
        description: "Access pre-screened professionals who match your specific requirements",
        icon: <StarIcon className="w-8 h-8" />,
        color: "purple",
    },
    {
        title: "Cost Effective",
        description: "Save on recruitment costs with our all-in-one hiring platform",
        icon: <ShieldCheckIcon className="w-8 h-8" />,
        color: "cyan",
    },
    {
        title: "User Friendly",
        description: "Intuitive interface designed for seamless navigation and ease of use",
        icon: <HeartIcon className="w-8 h-8" />,
        color: "pink",
    },
    {
        title: "Tailored Employee Recruitment Solutions",
        description:
            "Customized Job Matching with Industry-Specific Talent and End-to-End Hiring Support, From job posting, candidate screening, interviews to selection",
        icon: <MagnifyingGlassIcon className="w-8 h-8" />,
        color: "gray",
    },
    {
        title: "Employee Training & Self Development Programs",
        description:
            "Skill Enhancement: Training on job-specific skills and industry trends, ensuring employees stay competitive",
        icon: <AcademicCapIcon className="w-8 h-8" />,
        color: "blue",
    },
    {
        title: "HR Policy Creation & Consulting",
        description:
            "Tailored HR Policies: Develop policies aligned with your business values, goals, and legal compliance",
        icon: <ClipboardDocumentCheckIcon className="w-8 h-8" />,
        color: "pink",
    },
    {
        title: "Preparation for Future Success",
        description:
            "Future Workforce Planning: Guidance on hiring trends, future skill needs, and strategic workforce planning",
        icon: <LightBulbIcon className="w-8 h-8" />,
        color: "yellow",
    },
]

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
}

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
}

const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
}

export default function BenefitsSection() {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, amount: 0.1 })

    const bgPrimary = useColorModeValue("white", "gray.900")
    const textPrimary = useColorModeValue("gray.800", "white")
    const textSecondary = useColorModeValue("gray.600", "gray.300")

    return (
        <MotionBox
            as="section"
            py={{ base: 16, md: 24 }}
            bg={bgPrimary}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            <Box px={{ base: 6, md: 12, lg: 20 }} mx="auto" w="full" maxW="none">
                <MotionBox
                    ref={ref}
                    textAlign="center"
                    mb={12}
                    variants={fadeIn}
                >
                    <MotionBox
                        variants={fadeIn}
                    >
                        <Badge
                            colorScheme="purple"
                            bg="purple.100"
                            color="purple.800"
                            fontSize="sm"
                            px={3}
                            py={1}
                            borderRadius="full"
                            mb={4}
                        >
                            WHY CHOOSE US
                        </Badge>
                    </MotionBox>
                    <MotionHeading
                        as="h2"
                        fontSize={{ base: "3xl", md: "4xl" }}
                        fontWeight="bold"
                        color={textPrimary}
                        mb={4}
                        variants={fadeIn}
                    >
                        Benefits of Our{" "}
                        <Text
                            as="span"
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-transparent bg-clip-text inline"
                        >
                            Hiring Platform
                        </Text>
                    </MotionHeading>
                    <MotionText
                        fontSize={{ base: "lg", md: "xl" }}
                        color={textSecondary}
                        maxW="3xl"
                        mx="auto"
                        variants={fadeIn}
                    >
                        Discover how our platform can transform your recruitment process and help you find the perfect talent.
                    </MotionText>
                </MotionBox>

                <MotionSimpleGrid
                    columns={{ base: 1, md: 2, lg: 4 }}
                    spacing={8}
                    variants={staggerContainer}
                >
                    {benefits.map((benefit, index) => (
                        <MotionBox
                            key={index}
                            variants={itemVariant}
                            p={8}
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="xl"
                            bg="white"
                            boxShadow="sm"
                            whileHover={{
                                borderColor: `${benefit.color}.500`,
                                y: -8,
                                boxShadow: "lg",
                            }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <MotionFlex
                                align="center"
                                justify="center"
                                mb={6}
                                w={16}
                                h={16}
                                bg={`${benefit.color}.50`}
                                color={`${benefit.color}.500`}
                                borderRadius="full"
                                mx="auto"
                                whileHover={{ scale: 1.1 }}
                            >
                                {benefit.icon}
                            </MotionFlex>
                            <MotionHeading
                                as="h3"
                                fontSize="xl"
                                fontWeight="bold"
                                textAlign="center"
                                mb={4}
                                whileHover={{ color: `${benefit.color}.500` }}
                            >
                                {benefit.title}
                            </MotionHeading>
                            <Text color={textSecondary} textAlign="center" fontSize="md">
                                {benefit.description}
                            </Text>
                        </MotionBox>
                    ))}
                </MotionSimpleGrid>
            </Box>
        </MotionBox>
    )
}